import { Suspense, useCallback, useMemo, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { useIntl } from "react-intl";
import {
  Await,
  useLoaderData,
  useLocation,
  useNavigate,
  useNavigation,
  useSearchParams,
} from "react-router";

import type { ProductListPayload } from "./loader/category-loader.server";

import type { BuilderContent } from "@builder.io/sdk-react";

import Breadcrumbs, { BreadcrumbsSkeleton } from "~/components/breadcrumbs";
import { CategoryFilters } from "~/components/category-filters";
import { PlpErrorBoundaryView } from "~/components/error-boundries/plp-error-boundary";
import { SkeletonAwait } from "~/components/suspense/skeleton";
import { Skeleton } from "~/components/ui/skeleton";
import { useSite } from "~/contexts";
import {
  CioListingBeaconContext,
  type CioListingBeaconValue,
  useTrackCioResultsLoaded,
} from "~/hec/analytics/cio-listing-beacons";
import { ModelMap } from "~/hec/builder/model-map";
import { CioFiltersDrawerContent } from "~/hec/components/cio-filters-drawer-content";
import { FilterTags } from "~/hec/components/filter-tags/filter-tags";
import { FilterIcon } from "~/hec/components/plp-toolbar/filter-icon";
import { toolbarTriggerClass } from "~/hec/components/plp-toolbar/toolbar-trigger";
import { PrefetchedBuilderContent } from "~/hec/components/prefetched-builder-content";
import { PlpProductTile } from "~/hec/components/product/plp-product-tile";
import { ProductTileSkeleton } from "~/hec/components/product/product-tile-skeleton";
import { SeoCopyBlock } from "~/hec/components/seo-copy-block/seo-copy-block";
import { SortPopover } from "~/hec/components/sort-popover/sort-popover";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
} from "~/hec/components/ui/drawer";
import { LMN_ELIGIBILITY_FACET_KEY } from "~/hec/config/facet-mapping";
import {
  CioFacetProvider,
  type CioFacets,
  useCioFacetSelector,
} from "~/hec/context/cio-facet-adapter";
import { PlpRefinementContext } from "~/hec/context/plp-refinement-context";
import type { CategoryFiltersProps } from "~/hec/types/plp";
import { toFilterParams } from "~/hec/utils/cio-filter-params";
import { isFacetApplicable } from "~/hec/utils/facetHelpers";
import { type SiteId, bySiteId } from "~/hec/utils/site-id";
import { cn } from "~/lib/ui";
import { serializeSearchParams } from "~/lib/utils/url";
import {
  type PlpPaginationError,
  isPlpPaginationError,
} from "~/routes/dispatcher-plp-payload";
import { CLIENT_PAGINATION_PARAMS } from "~/routes/dispatcher-should-revalidate";
import { useTileImpressions } from "~/routes/resources/gtm-impressions";
import { useTrackProductsSearched } from "~/segment/page-events";

import { headers as headersDefinition } from "./components/headers";
import { InlineTileSlot } from "./components/inline-tile-slot";
import {
  type PlpInlineTileItem,
  interleaveInlineTiles,
  normalizeInlineSetupItems,
} from "./components/interleave-inline-tiles";
import {
  type MarketingTileItem,
  interleaveMarketingTiles,
} from "./components/interleave-marketing-tiles";
import { LoadFromStart } from "./components/load-from-start";
import { MarketingTileSlot } from "./components/marketing-tile-slot";
import {
  computeNextLoadMorePage,
  computeOffsetStart,
} from "./components/pagination-math";
import { PlpBottomBannerSection } from "./components/plp-bottom-banner";
import { PlpPagination } from "./components/plp-pagination";
import { SearchArticlesTab } from "./components/search-articles-tab";
import SearchNoResult from "./components/search-no-result";
import searchTabMessages from "./components/search-tab-messages";
import { Skeletons, SkeletonsFilters } from "./components/skeletons";
import type { SearchTab } from "./search-tabs";
import { SEARCH_TAB_PARAM, resolveSearchTab } from "./search-tabs";
import { dedupeByProductId } from "./utils/dedupe-hits";
import { useProductListData } from "./utils/product-list-hooks";
import { useAccumulatedGrid } from "./utils/use-accumulated-grid";
import { usePaginationFetcher } from "./utils/use-pagination-fetcher";

// "All [HSA|FSA] Eligible" subheading on PLP. WDH suppresses the
// eligibility subheading entirely (different product category).
const PLP_ELIGIBILITY_LABEL_BY_SITE: Record<SiteId, string | null> = {
  HSASTORE: "All HSA Eligible",
  FSASTORE: "All FSA Eligible",
  WDH: null,
};

// COM-158: first grid row (4 tiles at the widest breakpoint) is above the
// fold — those tile images load eager + fetchPriority="high" so the page has
// an LCP candidate immediately instead of sitting in lazy-load delay.
const ABOVE_FOLD_TILE_COUNT = 4;

export const headers = headersDefinition;

// Prop-based payload type for `CategoryPageContent`. `seo` is consumed by
// DeferSeo via the route match (`app/seo/seo.tsx:74`), so consumers don't
// read it from props. `pageType` is hoisted to the top level of the catch-
// all return so the root layout's useMatches scan finds it without
// descending into `payload`. Excluding both here keeps schema-dts's
// recursive JSON-LD types out of structural prop comparisons (which
// otherwise hit the TS "excessive stack depth" limit when this component
// is rendered from the catch-all dispatcher in `app/routes/$.tsx`).
export type CategoryPageData = Omit<ProductListPayload, "seo" | "pageType">;

function FilterButton({ onClick }: { onClick: () => void }) {
  const { state } = useCioFacetSelector(ctx => ctx);
  const filterCount = Object.values(state.selectedCioFacets).reduce(
    (sum, values) => sum + values.length,
    0,
  );

  return (
    <button
      type="button"
      onClick={onClick}
      className={toolbarTriggerClass}
      data-testid="filter-button"
    >
      <FilterIcon className="shrink-0" />
      Filter
      {filterCount > 0 && (
        <span className="ml-1 rounded-full bg-accent px-1.5 py-0.5 text-xs text-primary-foreground">
          {filterCount}
        </span>
      )}
    </button>
  );
}

function NoResultsSection({
  searchNoResultMessages,
  recomendationData,
  query,
}: {
  searchNoResultMessages: ProductListPayload["searchNoResultMessages"];
  recomendationData: ProductListPayload["recomendationData"];
  query: string | null;
}) {
  return (
    <div className="w-full py-8">
      <Suspense
        fallback={
          <SearchNoResult recomendationData={recomendationData} query={query} />
        }
      >
        <Await resolve={searchNoResultMessages}>
          {messages => (
            <SearchNoResult
              data={messages}
              recomendationData={recomendationData}
              query={query}
            />
          )}
        </Await>
      </Suspense>
    </div>
  );
}

// Resolved (non-promise) data shape. The loader's `data` field is a union of
// the listing payload and the SFCC-11462 error sentinel; `ResolvedPlpData` is
// the success arm rendered by `CategoryGridBody`.
type ResolvedCategoryData = Awaited<CategoryPageData["data"]>;
type ResolvedPlpData = Exclude<ResolvedCategoryData, PlpPaginationError>;
type Hit = NonNullable<ResolvedPlpData["hits"]>[number];

// `batchFor` + the history-state shape live with the accumulated-grid hook
// (`./utils/use-accumulated-grid`) so the hydration-safe seeding rule is
// independently unit-testable (COM-599).

function CategoryGridBody({
  data,
  currentPage,
  itemsPerPage,
  pageCap,
  viewAll,
  viewAllMaxResults,
  cioPageSize,
  marketingTileItems,
  inlineTiles,
  categoryId,
}: {
  data: ResolvedPlpData;
  currentPage: number;
  itemsPerPage: number;
  pageCap: number;
  viewAll: boolean;
  viewAllMaxResults: number;
  /** `num_results_per_page` of the CIO request that minted `data.result_id`. */
  cioPageSize: number;
  marketingTileItems: MarketingTileItem[];
  inlineTiles: PlpInlineTileItem[];
  categoryId?: string;
}) {
  const { site } = useSite();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // COM-599: seeded from the loader data exactly as the server renders it;
  // the accumulated grid stashed on the history entry (Load More / Start
  // Over) is restored in a post-hydration layout effect inside the hook.
  // Seeding `useState` from `location.state` on the initial POP made the
  // first client render diverge from the SSR HTML (72 tiles vs 36 + the
  // Start Over button) → React #418 at the grid boundary on every reload
  // after Load More.
  const {
    hits,
    setHits,
    gridStartPage,
    setGridStartPage,
    impressionBatch,
    setImpressionBatch,
  } = useAccumulatedGrid<Hit, ResolvedPlpData>({ data, currentPage });

  // ANALYTICS-19 (SFCC-11013): fire a `view_item_list` impression for the
  // current result-set. `list` is the PLP slug / "search" derived from the path.
  const impressionList =
    location.pathname.replace(/^\/+/, "").split("/").pop() || "plp";
  // SFCC-12141: shared by the impression push's item_list_name below and the
  // tile click's gtmListName further down, so both events attribute the same
  // list.
  const impressionListName = data.query ? "Search Results" : impressionList;
  // SFCC-11014: pass cgid + query so the impression endpoint also resolves the
  // Segment `View Category` (deduped endpoint-side to once per category/search).
  // SFCC-11798: viewport-gate the GA4 view_item_list push — report only products
  // actually scrolled into view, not the whole loaded page.
  // SFCC-11807: feed `impressionBatch` (the most recently loaded batch), not the
  // fixed `data.hits`, so Load More batches are fetched + observed too.
  useTileImpressions(impressionBatch.pids, impressionList, {
    cgid: categoryId,
    q: data.query,
    viewport: true,
    listName: impressionListName,
    indexOffset: impressionBatch.offset,
  });
  // SFCC-11014 (ANALYTICS-21): Segment `Products Searched`, once per query.
  useTrackProductsSearched(data.query, site.id);

  // SFCC-11906: Constructor.io browse/search behavioural beacons. `kind` is
  // driven by `categoryId` (browse) vs its absence (search) — the same signal
  // the grid uses for GTM list naming above. `beaconValue` is published via
  // context so a memoised `PlpProductTile` can fire the *_click beacon on tile
  // click; `useTrackCioResultsLoaded` fires the *_load beacon once per set.
  const beaconValue = useMemo<CioListingBeaconValue>(
    () => ({
      kind: categoryId ? "browse" : "search",
      term: data.query ?? "",
      categoryId: categoryId ?? "",
      resultId: data.result_id,
      resultCount: data.total,
      resultPage: currentPage,
      // COM-431: on `?page=all` the result_id was minted by a 200-result CIO
      // request, so report THAT page size, not the grid's 36.
      numResultsPerPage: cioPageSize,
    }),
    [
      categoryId,
      data.query,
      data.result_id,
      data.total,
      currentPage,
      cioPageSize,
    ],
  );
  const beaconItems = useMemo(
    () =>
      (data.hits ?? []).map(h => ({
        itemId: h.productId,
        itemName: h.productName,
      })),
    [data.hits],
  );
  useTrackCioResultsLoaded({ ...beaconValue, items: beaconItems });

  const hitsRef = useRef(hits);
  hitsRef.current = hits;

  // `signature`: pathname plus search params with the client-managed
  // pagination params (`page`, `range`) stripped. The pagination hook
  // snapshots this at click time and compares it again when the response
  // lands — a filter / sort / query click OR cross-category navigation
  // mid-flight changes the signature, and the hook drops the stale
  // response instead of stamping `?page=N` onto a new pathname.
  const filterSignature = useMemo(() => {
    const sp = new URLSearchParams(searchParams);
    for (const name of CLIENT_PAGINATION_PARAMS) sp.delete(name);
    // serializeSearchParams (sorted, literal comma) so the signature is
    // order-independent and matches the emitted URL form (SFCC-11657).
    return `${location.pathname}|${serializeSearchParams(sp)}`;
  }, [location.pathname, searchParams]);

  // Append Load More results, then push `?page=N+1` onto history. The
  // catch-all `shouldRevalidate` skips reval when only `page` changed, so
  // the loader does NOT re-run — `hits` stays as the merged array.
  const handleLoadMoreResolved = useCallback(
    ({
      resolved,
      targetUrl,
      error,
    }: {
      resolved: ResolvedPlpData | undefined;
      targetUrl: string;
      error?: boolean;
    }) => {
      // SFCC-11462 (R1/R3): the loader returned a pagination error sentinel.
      // Skip the merge so the accumulated grid stays mounted, and surface a
      // non-blocking toast — the Load More button remains visible to retry.
      if (error) {
        toast.error("Couldn't load more products. Please try again.");
        return;
      }
      const newHits = resolved?.hits ?? [];
      if (newHits.length === 0) return;
      // SFCC-11807: impress the newly loaded batch — feed its pids to the
      // impression endpoint so the viewport observer resolves + reports them.
      // Offset is read BEFORE the merge below, so it is the count of tiles
      // already rendered — i.e. the new batch's first tile is at `offset + 1`.
      //
      // SFCC-12175 W4: duplicates are excluded, because CIO can return the same
      // productId across page boundaries and `dedupeByProductId` keeps only the
      // first occurrence. Feeding a dupe would rank it (and shift every tile
      // after it) at a grid position it never occupies. `sentRef` in
      // `useViewportViewItemList` already dedupes by pid, so dropping the repeat
      // costs no impression.
      const alreadyRendered = new Set(hitsRef.current.map(h => h.productId));
      setImpressionBatch({
        pids: newHits
          .map(h => h.productId)
          .filter(id => Boolean(id) && !alreadyRendered.has(id)),
        offset: hitsRef.current.length,
      });
      const merged = dedupeByProductId([...hitsRef.current, ...newHits]);
      setHits(merged);
      navigate(targetUrl, {
        state: { accumulatedHits: merged, gridStartPage },
        preventScrollReset: true,
      });
    },
    [navigate, gridStartPage],
  );
  const { trigger: triggerLoadMore, isLoading: isLoadingMore } =
    usePaginationFetcher<ResolvedPlpData>({
      key: "plp-load-more",
      filterSignature,
      onResolved: handleLoadMoreResolved,
    });

  // Prepend pages 1..(currentPage-1) — fetched as one `?range=N` call that
  // reuses the existing fan-out path in the loader. The URL drop to the
  // canonical category path happens eagerly in `handleStartOver`; this
  // callback just merges the response into the grid and replaces the
  // current history entry's state so back-nav restores the full set.
  const handleStartOverResolved = useCallback(
    ({
      resolved,
      targetUrl,
      error,
    }: {
      resolved: ResolvedPlpData | undefined;
      targetUrl: string;
      error?: boolean;
    }) => {
      // SFCC-11462 (R1/R3): pagination error sentinel on the prepend fetch —
      // keep the current grid, toast, and leave Start From The Beginning
      // available to retry.
      if (error) {
        toast.error("Couldn't load earlier products. Please try again.");
        return;
      }
      const earlierHits = resolved?.hits ?? [];
      if (earlierHits.length === 0) return;
      const merged = dedupeByProductId([...earlierHits, ...hitsRef.current]);
      // SFCC-12175 W4: "Start From The Beginning" PREPENDS earlier pages, so
      // every tile already in the grid moves further down it. Without shifting
      // the offset, tiles that scroll into view after this point report an index
      // understated by the prepended count. Safe to re-set: the viewport hook's
      // resetKey is [list, cgid, q] and excludes the offset, so already-sent
      // tiles are not re-pushed.
      const prepended = merged.length - hitsRef.current.length;
      if (prepended > 0) {
        setImpressionBatch(b => ({ ...b, offset: b.offset + prepended }));
      }
      setHits(merged);
      navigate(targetUrl, {
        state: { accumulatedHits: merged, gridStartPage: 1 },
        replace: true,
        preventScrollReset: true,
      });
    },
    [navigate],
  );
  const { trigger: triggerStartOver, isLoading: isStartingOver } =
    usePaginationFetcher<ResolvedPlpData>({
      key: "plp-start-over",
      filterSignature,
      onResolved: handleStartOverResolved,
    });

  const handleLoadMore = () => {
    if (isLoadingMore) return;
    const nextPage = computeNextLoadMorePage({
      hitsCount: hits.length,
      gridStartPage,
      itemsPerPage,
      pageCap,
    });
    // Grid already holds the last reachable page (post-Start-Over full grid).
    if (nextPage === null) return;
    const params = new URLSearchParams(searchParams);
    params.set("page", String(nextPage));
    const targetUrl = `${location.pathname}?${serializeSearchParams(params)}`;
    triggerLoadMore(targetUrl, targetUrl);
  };

  const handleStartOver = () => {
    if (isStartingOver || currentPage <= 1) return;

    // Drop the `page` param eagerly so the URL is canonical the moment the
    // user clicks. The loader is skipped via `shouldRevalidate` because
    // only `page` differs between current and next URL.
    const cleanParams = new URLSearchParams(searchParams);
    cleanParams.delete("page");
    const cleanQs = serializeSearchParams(cleanParams);
    const canonicalUrl = `${location.pathname}${cleanQs ? `?${cleanQs}` : ""}`;
    navigate(canonicalUrl, {
      state: { accumulatedHits: hitsRef.current, gridStartPage: 1 },
      preventScrollReset: true,
    });
    setGridStartPage(1);

    // Fan-out pages 1..(currentPage-1) via the loader's `range` mode. The
    // result is merged into the grid in the start-over onResolved callback.
    const fetchParams = new URLSearchParams(searchParams);
    fetchParams.delete("page");
    fetchParams.set("range", String(currentPage - 1));
    triggerStartOver(
      `${location.pathname}?${serializeSearchParams(fetchParams)}`,
      canonicalUrl,
    );
  };

  const totalShowing = hits.length;
  const total = data.total;
  const totalPages = Math.max(1, Math.ceil(total / itemsPerPage));
  // 1-indexed grid position per productId (first occurrence wins, matching the
  // `findIndex` this replaces). COM-431: the tile maps below used to run
  // `hits.findIndex` per tile — O(n²) per render, ~1M compares on a 1,000-tile
  // View All grid, on SSR and again on every client re-render.
  const positionById = useMemo(() => {
    const map = new Map<string, number>();
    hits.forEach((h, i) => {
      if (h.productId && !map.has(h.productId)) map.set(h.productId, i + 1);
    });
    return map;
  }, [hits]);
  const offsetStart = computeOffsetStart({
    gridStartPage,
    itemsPerPage,
    viewAll,
  });

  // Start Over CTA only shows when the user landed deep (?page=N>1) and
  // the grid hasn't been anchored back to product #1.
  const showStartOver = !viewAll && gridStartPage > 1;

  return (
    // SFCC-11906: publishes the CIO listing beacon context to descendant tiles.
    // A context provider adds no DOM node, so the tiles stay direct children of
    // the CSS grid.
    <CioListingBeaconContext value={beaconValue}>
      {showStartOver && (
        <LoadFromStart onClick={handleStartOver} loading={isStartingOver} />
      )}
      {/* SFCC-11462 (R2): prepend a page of skeleton tiles while Start From The
          Beginning is fetching the earlier pages. Count matches itemsPerPage. */}
      {isStartingOver &&
        Array.from({ length: itemsPerPage }).map((_, i) => (
          <ProductTileSkeleton key={`startover-skeleton-${i}`} />
        ))}
      {inlineTiles.length > 0
        ? // New PLP Inline Content path (SFCC-11609): absolute, stable,
          // per-breakpoint cell slots; the grid uses row flow (no dense).
          interleaveInlineTiles(hits, inlineTiles).map(entry => {
            if (entry.kind !== "product") {
              return (
                <InlineTileSlot
                  key={`tile-${entry.key}`}
                  tile={entry.tile}
                  spans={entry.spans}
                />
              );
            }
            const position = positionById.get(entry.product.productId) ?? 0;
            return (
              <PlpProductTile
                key={`hit-${entry.key}`}
                product={entry.product}
                gtmListId={impressionList}
                gtmListName={impressionListName}
                gtmIndex={position}
                // COM-158: first row of tiles is above the fold — eager +
                // fetchPriority="high" so an LCP candidate exists at load.
                imagePriority={position <= ABOVE_FOLD_TILE_COUNT}
              />
            );
          })
        : // Legacy category-marketing-tiles path (dual-read fallback).
          interleaveMarketingTiles(hits, marketingTileItems).map(entry => {
            if (entry.kind !== "product") {
              return (
                <MarketingTileSlot
                  key={`tile-${entry.key}`}
                  tile={entry.tile}
                />
              );
            }
            const position = positionById.get(entry.product.productId) ?? 0;
            return (
              <PlpProductTile
                key={`hit-${entry.key}`}
                product={entry.product}
                gtmListId={impressionList}
                gtmListName={impressionListName}
                gtmIndex={position}
                // COM-158: first row of tiles is above the fold — eager +
                // fetchPriority="high" so an LCP candidate exists at load.
                imagePriority={position <= ABOVE_FOLD_TILE_COUNT}
              />
            );
          })}
      {/* SFCC-11462 (R2): append a page of skeleton tiles while Load More is in
          flight. Count matches itemsPerPage (the incoming page size). */}
      {isLoadingMore &&
        Array.from({ length: itemsPerPage }).map((_, i) => (
          <ProductTileSkeleton key={`loadmore-skeleton-${i}`} />
        ))}
      {totalShowing > 0 && (
        <PlpPagination
          currentPage={currentPage}
          totalPages={totalPages}
          viewAll={viewAll}
          pageCap={pageCap}
          total={total}
          shown={totalShowing}
          itemsPerPage={itemsPerPage}
          viewAllMaxResults={viewAllMaxResults}
          offsetStart={offsetStart}
          onLoadMore={handleLoadMore}
          isLoadingMore={isLoadingMore}
        />
      )}
    </CioListingBeaconContext>
  );
}

/**
 * Prop-based render of the category page. Used directly by the catch-all
 * dispatcher (`app/routes/$.tsx`) when `lookupUrlMapping` resolves to a
 * CATEGORY hit — that route can't use `useLoaderData<typeof loader>()`
 * because the dispatcher's loader returns a discriminated union, not the
 * PLP shape.
 *
 * The default `CategoryPage` export below remains the entry point for the
 * `/search` route (which has its own loader but reuses this component).
 */
export function CategoryPageContent({
  loaderData,
  plpBottomBannerPreview,
  clampGridForPreview,
}: {
  loaderData: CategoryPageData;
  /**
   * `/edit/plp-bottom-banner/:id` only: the live editor entry, rendered
   * eagerly in the bottom slot instead of the scroll-lazy fetch.
   */
  plpBottomBannerPreview?: BuilderContent | null;
  /**
   * Builder editor preview routes only (`/edit/plp-top-banner/:id`,
   * `/edit/plp-bottom-banner/:id`): cap the product grid's height so the
   * author sees the edited section in context without a full-length grid
   * dominating the preview iframe.
   */
  clampGridForPreview?: boolean;
}) {
  const {
    data,
    itemsPerPage,
    $page,
    viewAll,
    pageCap,
    viewAllMaxResults,
    cioPageSize,
    categories,
    categoryDetail,
    pageH1,
    recomendationData,
    searchNoResultMessages,
    marketingTiles,
    refinementFilters,
    plpInlineSetup,
    categoryFilters,
    plpTopBannerSection,
    subcategories,
    searchArticles,
    facetMapping,
  } = loaderData;

  // Read currentPage from the URL — Load More navigates with
  // `?page=N` but `shouldRevalidate` skips the loader, so `$page` from
  // loaderData stays at whatever the initial render saw (usually 1). The
  // URL is the source of truth for "how deep have we paginated."
  // Clamped to `pageCap` so a `?page=500` URL doesn't blow the counter
  // offsets — the server clamps the same way (numeric page>cap renders page
  // `pageCap`; View All is only ever the literal `?page=all`).
  // The setter is load-bearing for SFCC-11735's `?tab=` persistence — develop
  // only reads the params here, this branch also writes them.
  const [pageSearchParams, setPageSearchParams] = useSearchParams();
  const intl = useIntl();
  // SFCC-11958: the refinement an in-grid Builder carousel must browse under —
  // the grid's OWN CIO filter set, straight from the loader, so the two can
  // never diverge. Serialised in CIO's `filters[key]=value` wire form; a
  // string, so it compares by value and `page` / `sort` / `range` (which never
  // reach `refinementFilters`) leave it untouched.
  const refinements = useMemo(
    () => serializeSearchParams(toFilterParams(refinementFilters)),
    [refinementFilters],
  );
  const urlPageRaw = pageSearchParams.get("page");
  const currentPage =
    urlPageRaw === "all"
      ? typeof $page === "number"
        ? $page
        : 1
      : Math.max(1, Math.min(pageCap, parseInt(urlPageRaw ?? "1") || 1));

  const { query } = useProductListData();
  const { site } = useSite();
  const eligibilityLabel = site
    ? bySiteId(site.id, PLP_ELIGIBILITY_LABEL_BY_SITE)
    : null;

  const marketingTileItems: MarketingTileItem[] =
    (
      marketingTiles as
        | { data?: { injectedContentItems?: MarketingTileItem[] } }
        | null
        | undefined
    )?.data?.injectedContentItems ?? [];

  // New PLP Inline Content placement (SFCC-11609). When a `plp-inline-content-setup`
  // entry exists it takes precedence over the legacy marketing tiles. The grid
  // keeps `grid-auto-flow: dense` (as the legacy path does) so multi-column tiles
  // pack without leaving row-wrap gaps; absolute-slot stability holds at the
  // DOM-order level (see interleaveInlineTiles) — the migration reproduces the
  // legacy DOM order, so dense renders it identically to today.
  const inlineTiles = useMemo(
    () => (plpInlineSetup ? normalizeInlineSetupItems(plpInlineSetup) : []),
    [plpInlineSetup],
  );

  const location = useLocation();
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const pathSegments = location.pathname.split("/").filter(Boolean);
  const isSubcategory = pathSegments.length > 2; // /shop/slug = 2 segments

  const navigation = useNavigation();
  const isFiltering =
    navigation.state === "loading" &&
    navigation.location?.pathname === location.pathname;

  // SFCC-11499: Articles tab on the search page. The loader populates
  // `searchArticles` only on /search and only for sites with an Articles index
  // (null on WDH by site id, and on every category PLP). The Products tab is
  // always present; the Articles tab renders ONLY when CIO returned a count > 0.
  const showArticlesTab = !!searchArticles && searchArticles.total > 0;
  // SFCC-11735: the active tab is URL-driven (`?tab=`) so it's shareable,
  // deep-linkable, and survives reload + browser back/forward. `resolveSearchTab`
  // defaults to Products and defensively falls back to Products if a query change
  // drops the Articles tab (or `?tab=articles` is deep-linked with no articles),
  // so we never strand the user on a hidden tab. The switch is a pure client
  // view toggle — `searchShouldRevalidate` keeps the loader from re-running.
  const currentSearchTab = resolveSearchTab(
    pageSearchParams.get(SEARCH_TAB_PARAM),
    showArticlesTab,
  );
  const selectSearchTab = (tab: SearchTab) => {
    // Clicking the already-active tab would push a byte-identical URL. RR7
    // revalidates same-URL navigations by default and root + layout both
    // return true there, so every redundant click costs a `.data` round-trip
    // that re-runs the expensive layout loader (category tree + Builder chrome
    // + SLAS auth) — plus a duplicate history entry that traps the Back button.
    if (tab === currentSearchTab) return;
    setPageSearchParams(
      (prev: URLSearchParams) => {
        const next = new URLSearchParams(prev);
        // Products is the default → clean canonical URL (no param); only
        // Articles writes `?tab=articles`. Push (no `replace`) so Back returns
        // to the previous tab.
        if (tab === "articles") next.set(SEARCH_TAB_PARAM, "articles");
        else next.delete(SEARCH_TAB_PARAM);
        return next;
      },
      { preventScrollReset: true },
    );
  };

  return (
    <section className="min-[992px]:mx-auto min-[992px]:w-full min-[992px]:px-[87px] min-[1200px]:max-w-[1458px] mx-[3px] bg-white">
      <div className="container mx-auto flex flex-col pb-12">
        <h2 className="hidden">Products list page</h2>

        {/* PLP Top Banner — renders under the top nav, above the
            breadcrumbs and page title, for parity with SFRA (SFCC-11356).
            Kept outside <nav id="store"> so the marketing banner doesn't
            live inside a navigation landmark; px matches the breadcrumb/
            grid inset it previously inherited from the nav. */}
        {plpTopBannerSection && (
          <div
            data-testid="plp-top-banner"
            className="w-full px-2 pb-2 sm:px-6"
          >
            {/* Section-level Suspense so a suspending Builder custom component
                (e.g. a React.lazy block) is contained here and CANNOT bubble
                past the route <Outlet> into a page-level skeleton. */}
            <Suspense fallback={null}>
              <PrefetchedBuilderContent
                model={ModelMap.plpTopBanner}
                content={plpTopBannerSection}
              />
            </Suspense>
          </div>
        )}

        <nav id="store" className="top-0 w-full px-2 pb-2 pt-4 sm:px-6">
          <SkeletonAwait
            resolve={categories}
            skeleton={<BreadcrumbsSkeleton />}
          >
            {categories => (
              <Breadcrumbs
                categories={categories}
                isSearchResult={!!query}
                searchQuery={query ?? undefined}
              />
            )}
          </SkeletonAwait>

          {/* Page heading — single visible <h1> above the filter row,
              independent from the breadcrumb (PLP-CONTENT-04, SFCC-10375,
              SFCC-10757). Always populated by the loader: SCAPI category
              name (or humanised id fallback) for category/brand PLPs,
              `Search results for "<q>"` for search. */}
          <SkeletonAwait
            resolve={pageH1}
            skeleton={<Skeleton className="mb-2 mt-3 h-8 w-48" />}
          >
            {name => (
              <h1 className="mb-2 mt-3 text-left text-2xl font-bold text-blue-03">
                {name}
              </h1>
            )}
          </SkeletonAwait>

          {/* SFCC-11499: Products / Articles tab bar. Only rendered on the
              search route when CIO returned Articles (count > 0); hidden on WDH
              (loader returns null searchArticles by site id) and on every
              category PLP. Products is always the default/left tab. */}
          {showArticlesTab && searchArticles && (
            <div
              role="tablist"
              aria-label="Search result types"
              data-testid="search-result-tabs"
              className="mt-3 flex gap-6 border-b border-gray-200"
            >
              <button
                type="button"
                role="tab"
                id="search-tab-products"
                aria-selected={currentSearchTab === "products"}
                aria-controls="search-panel-products"
                data-testid="search-tab-products"
                onClick={() => selectSearchTab("products")}
                className={cn(
                  "-mb-px border-b-2 pb-2 font-grotesque text-sm font-medium",
                  currentSearchTab === "products"
                    ? "border-accent text-blue-03"
                    : "border-transparent text-blue-04 hover:text-blue-03",
                )}
              >
                {intl.formatMessage(searchTabMessages.productsTab)}
              </button>
              <button
                type="button"
                role="tab"
                id="search-tab-articles"
                aria-selected={currentSearchTab === "articles"}
                aria-controls="search-panel-articles"
                data-testid="search-tab-articles"
                onClick={() => selectSearchTab("articles")}
                className={cn(
                  "-mb-px border-b-2 pb-2 font-grotesque text-sm font-medium",
                  currentSearchTab === "articles"
                    ? "border-accent text-blue-03"
                    : "border-transparent text-blue-04 hover:text-blue-03",
                )}
              >
                {intl.formatMessage(searchTabMessages.articlesTab, {
                  count: searchArticles.total,
                })}
              </button>
            </div>
          )}

          {/* HEC PLP toolbar with real CIO-driven components */}
          {currentSearchTab === "products" && (
            <SkeletonAwait resolve={data} skeleton={<SkeletonsFilters />}>
              {data =>
                isPlpPaginationError(data) ? null : (
                  <CioFacetProvider
                    facets={(data.cioFacets ?? []) as CioFacets[]}
                    sortOptions={data.cioSortOptions ?? []}
                    totalResults={data.total}
                    siteId={site?.id}
                    // BM `Constructor_FacetMapping` from the loader: the drawer
                    // facet list/labels and the client allowlist come from the
                    // same value the server filtered with.
                    facetMapping={facetMapping}
                    listId={
                      location.pathname.replace(/^\/+/, "").split("/").pop() ||
                      "plp"
                    }
                  >
                    {!isSubcategory && categoryFilters?.data && (
                      <CategoryFilters
                        {...(categoryFilters.data as CategoryFiltersProps)}
                        subcategories={subcategories}
                      />
                    )}
                    <div className="container mx-auto mt-0 flex flex-col items-center justify-between">
                      {/* Filter + Sort row — Filter (left) | Count (center) |
                        Sort (right). Always rendered, even on a zero-result
                        filter (e.g. a too-narrow price range): the controls must
                        stay so the shopper can adjust or clear the filter instead
                        of being stranded on a bare no-results page. The count
                        reads "0 Products" in that case. */}
                      <div className="flex w-full flex-col gap-1">
                        <div className="flex w-full items-center justify-between gap-3 px-2.5 py-1">
                          <FilterButton
                            onClick={() => setFilterDrawerOpen(true)}
                          />

                          <div className="text-center">
                            <p className="font-grotesque text-[13px] font-light leading-[19.5px] text-blue-04">
                              <span>{data.total}</span>{" "}
                              {data.total === 1 ? "Product" : "Products"}
                            </p>
                            {data.total > 0 &&
                              eligibilityLabel &&
                              !isFacetApplicable(
                                data.cioFacets as CioFacets[] | undefined,
                                LMN_ELIGIBILITY_FACET_KEY,
                              ) && (
                                <p className="font-grotesque text-[14px] font-light leading-[19.5px] text-blue-04">
                                  {eligibilityLabel}
                                </p>
                              )}
                          </div>

                          <SortPopover />
                        </div>

                        {/* Applied filter tags (desktop) */}
                        <FilterTags className="mb-3 hidden md:flex" />
                        {/* On mobile the applied-filter tags normally live only
                          inside the filter drawer, but a zero-result page needs
                          an inline way to remove the offending filter without
                          opening the drawer — surface them here when the grid is
                          empty so the shopper can fix the results in place. */}
                        {data.total === 0 && (
                          <FilterTags className="mb-3 flex flex-wrap md:hidden" />
                        )}
                      </div>

                      {/* Filter drawer */}
                      <Drawer
                        open={filterDrawerOpen}
                        onOpenChange={setFilterDrawerOpen}
                        direction="left"
                      >
                        <DrawerContent data-testid="filter-drawer-content">
                          <div className="flex h-screen-dynamic flex-col">
                            <DrawerHeader heading="Filters" />
                            <CioFiltersDrawerContent
                              onOpenChange={() => setFilterDrawerOpen(false)}
                            />
                          </div>
                        </DrawerContent>
                      </Drawer>

                      {/* Zero results: friendly message + recommendations carousel,
                        below the still-present filter controls. Gated on the
                        authoritative resolved `data.total` so it shows the same
                        way on a hard URL load and a client-side filter apply. */}
                      {data.total === 0 && (
                        <NoResultsSection
                          searchNoResultMessages={searchNoResultMessages}
                          recomendationData={recomendationData}
                          query={query}
                        />
                      )}
                    </div>
                  </CioFacetProvider>
                )
              }
            </SkeletonAwait>
          )}

          {/* SFCC-11499: Articles tab panel. Renders the CIO Articles list when
              the Articles tab is active (search route only). The Products grid
              below is suppressed while this tab is selected. */}
          {currentSearchTab === "articles" && searchArticles && (
            <div
              role="tabpanel"
              id="search-panel-articles"
              aria-labelledby="search-tab-articles"
              data-testid="search-articles-panel"
              className="mt-4"
            >
              <SearchArticlesTab articles={searchArticles} />
            </div>
          )}
        </nav>

        {/* Product list — fade while filtering. Suppressed on the Articles
            tab (SFCC-11499) so the article list is the only result surface. */}
        {currentSearchTab === "products" && (
          <div
            id="search-panel-products"
            role={showArticlesTab ? "tabpanel" : undefined}
            aria-labelledby={
              showArticlesTab ? "search-tab-products" : undefined
            }
            className={cn(
              "grid w-full grid-cols-1 gap-x-4 gap-y-6 px-2 transition-opacity duration-200 [grid-auto-flow:dense] sm:grid-cols-2 sm:px-6 md:grid-cols-3 xl:grid-cols-4",
              isFiltering ? "pointer-events-none opacity-50" : "opacity-100",
              // Editor preview only: keep the grid short so the section being
              // edited stays in view instead of a full-length product grid.
              clampGridForPreview && "max-h-[560px] overflow-hidden",
            )}
          >
            <SkeletonAwait
              resolve={data}
              skeleton={<Skeletons size={itemsPerPage} />}
            >
              {data => {
                // SFCC-11462: a listing failure surfaces as the error-as-data
                // sentinel in loaderData (deep `?page=N` document load, View
                // All `<Link>` nav, or page-1). Render the PLP error UI inline,
                // full-width — keeps the page chrome (breadcrumb / h1), no blank
                // grid and no PDP boundary. (Load More / Start-Over failures
                // ride the fetcher → toast + grid, never reaching here.)
                if (isPlpPaginationError(data)) {
                  return (
                    <div className="col-span-full">
                      <PlpErrorBoundaryView />
                    </div>
                  );
                }
                // Zero results: render an empty grid — no products, and crucially
                // no interleaved Builder marketing / inline-content tiles (which
                // would otherwise float as orphaned injected content). The
                // no-results message + recommendations carousel render above,
                // under the still-present filter controls. Gated on the resolved
                // `data.total` so a client-side filter apply behaves like a hard
                // URL load instead of leaving the prior grid mounted.
                if (data.total === 0) return null;
                return (
                  // SFCC-11958: publishes the active refinement to the grid's
                  // subtree so a Builder carousel authored into an inline tile
                  // browses CIO under the same filters as the cell it occupies.
                  // Wrapped at the call site so the grid's children stay direct
                  // children of the CSS grid (a context renders no DOM anyway).
                  <PlpRefinementContext value={refinements}>
                    <CategoryGridBody
                      data={data}
                      currentPage={currentPage}
                      itemsPerPage={itemsPerPage}
                      pageCap={pageCap}
                      viewAll={viewAll}
                      viewAllMaxResults={viewAllMaxResults}
                      cioPageSize={cioPageSize}
                      marketingTileItems={marketingTileItems}
                      inlineTiles={inlineTiles}
                      categoryId={loaderData.categoryId}
                    />
                  </PlpRefinementContext>
                );
              }}
            </SkeletonAwait>
          </div>
        )}
        {/* end filtering opacity wrapper */}
      </div>

      {/* PLP Bottom Banner — the Top Banner's counterpart under the grid:
          after the Load More button, above the SEO copy. Scroll-lazy on live
          PLPs (no loader fetch); eager in the Builder editor preview. */}
      <PlpBottomBannerSection
        categoryId={loaderData.categoryId}
        previewContent={plpBottomBannerPreview}
      />

      {/* SEO copy — below grid, matching production placement.
          Category H1 lives above the filter row (see top of route). */}
      <Suspense>
        <Await resolve={categoryDetail}>
          {detail =>
            detail?.seoAdditionalCopy ? (
              <SeoCopyBlock
                seoAdditionalCopy={detail.seoAdditionalCopy}
                heading={detail.name?.trim() || undefined}
              />
            ) : null
          }
        </Await>
      </Suspense>
    </section>
  );
}

/**
 * Default route component — used by the legacy `/shop/.../:categoryId`
 * route. Pulls loader data via the React Router hook and forwards to
 * the prop-based `CategoryPageContent` (also re-used by the catch-all
 * dispatcher when a slug resolves to a CATEGORY).
 */
export default function CategoryPage() {
  const loaderData = useLoaderData<ProductListPayload>();
  return <CategoryPageContent loaderData={loaderData} />;
}

// SFCC-11462 (R4): shared PLP error UI, used by the `/search` + legacy
// `/shop/:categoryId` routes and by the dispatcher's page-type-aware boundary.
export function ErrorBoundary() {
  return <PlpErrorBoundaryView />;
}
